import { useCallback } from 'react'
import { appsyncQuery } from '../../../api/appsync-client.js'
import { CREATE_TASK, DELETE_TASK, UPDATE_TASK } from '../graphql.js'
import { nextAssignee, nextPriority, nextStatus } from '../utils/cycling.js'

/**
 * Wraps the task mutations with optimistic-update + rollback logic.
 *
 * The hook is initialized with the same internal mutators that
 * `useTaskList` returns under `_*` keys. We never call `setLiveTasks`
 * directly; we always go through those mutators so the source of truth
 * stays in `useTaskList`.
 *
 * On success: the subscription will deliver the canonical update and
 * reconcile naturally — no extra work needed.
 * On failure: roll the optimistic change back and surface the error via
 * the `onError` callback so the parent can render an inline indicator.
 */
export function useTaskMutations({
  reportId,
  managerId,
  currentUserId,
  list, // the object returned by useTaskList
  onError,
}) {
  const { _optimisticUpdate, _optimisticInsert, _optimisticRemove, _snapshotState, _restoreSnapshot } = list

  const reportError = useCallback(
    (err) => {
      if (onError) onError(err)
    },
    [onError],
  )

  const createTask = useCallback(
    async ({ title, assignee = currentUserId, status = 'TODO', priority = 'MEDIUM', dueDate = null, description = '' }) => {
      const tempId = `tmp-${Math.random().toString(36).slice(2, 10)}`
      const now = new Date().toISOString()
      const optimistic = {
        taskId: tempId,
        reportId,
        managerId,
        assignee,
        title,
        description,
        status,
        priority,
        dueDate,
        createdAt: now,
        updatedAt: now,
        completedAt: null,
        createdBy: currentUserId,
        isOrphaned: false,
      }
      const before = _snapshotState()
      _optimisticInsert(optimistic)
      try {
        const data = await appsyncQuery(CREATE_TASK, {
          input: { reportId, managerId, assignee, title, description, status, priority, dueDate },
        })
        const real = data?.createTask
        if (real) {
          // Replace the temp insertion with the real record.
          _optimisticRemove(tempId)
          _optimisticInsert(real)
        }
        return real
      } catch (err) {
        _restoreSnapshot(before)
        reportError(err)
        // Do not re-throw — the inline error indicator is the user-facing
        // signal and the local state has already rolled back. Re-throwing
        // would surface as an unhandled rejection in onClick handlers.
        return null
      }
    },
    [reportId, managerId, currentUserId, _optimisticInsert, _optimisticRemove, _snapshotState, _restoreSnapshot, reportError],
  )

  const updateTask = useCallback(
    async (taskId, patch) => {
      const before = _snapshotState()
      _optimisticUpdate(taskId, { ...patch, updatedAt: new Date().toISOString() })
      try {
        const data = await appsyncQuery(UPDATE_TASK, {
          input: { taskId, reportId, ...patch },
        })
        return data?.updateTask
      } catch (err) {
        _restoreSnapshot(before)
        reportError(err)
        return null
      }
    },
    [reportId, _optimisticUpdate, _snapshotState, _restoreSnapshot, reportError],
  )

  const deleteTask = useCallback(
    async (taskId) => {
      const before = _snapshotState()
      _optimisticRemove(taskId)
      try {
        await appsyncQuery(DELETE_TASK, { taskId, reportId })
      } catch (err) {
        _restoreSnapshot(before)
        reportError(err)
      }
    },
    [reportId, _optimisticRemove, _snapshotState, _restoreSnapshot, reportError],
  )

  const cycleStatus = useCallback(
    (task) => updateTask(task.taskId, { status: nextStatus(task.status) }),
    [updateTask],
  )
  const cyclePriority = useCallback(
    (task) => updateTask(task.taskId, { priority: nextPriority(task.priority) }),
    [updateTask],
  )
  const cycleAssignee = useCallback(
    (task) => updateTask(task.taskId, { assignee: nextAssignee(task.assignee, reportId, managerId) }),
    [updateTask, reportId, managerId],
  )

  return { createTask, updateTask, deleteTask, cycleStatus, cyclePriority, cycleAssignee }
}
